import React from 'react'
import { Button, Separator } from '@/shared/ui'
import RelatorioV2Filters, { type RelatorioV2UiFilters } from '../components/RelatorioV2Filters'
import OutcomeSummaryCards from '../components/OutcomeSummaryCards'
import OutcomeDistributionTable from '../components/OutcomeDistributionTable'
import OutcomeBreakdownTable from '../components/OutcomeBreakdownTable'
import { fetchRelatorioV2Dataset, type RelatorioV2Row } from '../services/relatorioV2Service'
import { computeMetrics } from '../utils/metrics'

function todayISODate(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function daysAgoISODate(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - Math.max(0, Math.floor(days)))
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default function RelatorioV2Page() {
  const [filters, setFilters] = React.useState<RelatorioV2UiFilters>(() => ({
    closedFrom: daysAgoISODate(30),
    closedTo: todayISODate(),
    maxRows: 5000,
    symbol: '',
    targetTimeframe: '',
    targetTrend: '',
    opportunityType: '',
  }))

  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [rows, setRows] = React.useState<RelatorioV2Row[]>([])
  const [truncated, setTruncated] = React.useState(false)
  const [totalMatched, setTotalMatched] = React.useState<number | null>(null)

  const filteredRows = React.useMemo(() => applyClientFilters(rows, filters), [rows, filters])
  const metrics = React.useMemo(() => computeMetrics(filteredRows), [filteredRows])

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const ds = await fetchRelatorioV2Dataset({
        closedFrom: filters.closedFrom || null,
        closedTo: filters.closedTo || null,
        maxRows: filters.maxRows,
      })
      setRows(ds.rows)
      setTruncated(ds.truncated)
      setTotalMatched(ds.totalMatched)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar relatório')
      setRows([])
      setTruncated(false)
      setTotalMatched(null)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const matchedHint = totalMatched != null
    ? `Encontradas ${totalMatched.toLocaleString('pt-BR')} linhas (dataset carregado: ${rows.length.toLocaleString('pt-BR')}${truncated ? ', truncado pelo limite' : ''}). Filtros de símbolo/TF/tendência/tipo são aplicados no client.`
    : null

  return (
    <div className="mx-auto max-w-[1200px] space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Relatório V2</h1>
        <p className="text-sm text-foreground/70">
          Assertividade das oportunidades encerradas pelo módulo de Sinais (TP1/TP2/TP3 e Stop). Encerramentos por entry_missed contam como Alvo 3.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}

      <RelatorioV2Filters value={filters} onChange={setFilters} onSubmit={load} loading={loading} matchedHint={matchedHint} />

      <OutcomeSummaryCards metrics={metrics} />

      <div className="grid gap-4 grid-cols-1">
        <OutcomeDistributionTable metrics={metrics} />
        <OutcomeBreakdownTable
          rows={filteredRows}
          def={{
            title: 'Cruzamento: por TF alvo (target_timeframe)',
            keyLabel: 'TF',
            getKey: (r) => String(r.targetTimeframe || '(sem TF)'),
          }}
        />
      </div>

      <Separator />

      <div className="grid gap-4 grid-cols-1">
        <OutcomeBreakdownTable
          rows={filteredRows}
          def={{
            title: 'Cruzamento: por tendência (target_trend)',
            keyLabel: 'Tendência',
            getKey: (r) => String(r.targetTrend || '(sem tendência)'),
          }}
        />
        <OutcomeBreakdownTable
          rows={filteredRows}
          def={{
            title: 'Cruzamento: por tipo (opportunity_type)',
            keyLabel: 'Tipo',
            getKey: (r) => String(r.opportunityType || '(sem tipo)'),
            top: 30,
          }}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-1">
        <OutcomeBreakdownTable
          rows={filteredRows}
          def={{
            title: 'Cruzamento: por símbolo (top 30)',
            keyLabel: 'Símbolo',
            getKey: (r) => String(r.symbol || '(sem símbolo)'),
            top: 30,
          }}
        />
      </div>

      <div className="flex gap-2">
        <Button onClick={load} disabled={loading}>
          {loading ? 'Atualizando…' : 'Atualizar'}
        </Button>
      </div>
    </div>
  )
}

function applyClientFilters(rows: RelatorioV2Row[], filters: RelatorioV2UiFilters): RelatorioV2Row[] {
  const sym = String(filters.symbol || '').trim().toUpperCase()
  const tf = String(filters.targetTimeframe || '').trim().toUpperCase()
  const trend = String(filters.targetTrend || '').trim().toLowerCase()
  const type = String(filters.opportunityType || '').trim().toLowerCase()

  return rows.filter((r) => {
    if (sym) {
      const rs = String(r.symbol || '').trim().toUpperCase()
      if (rs !== sym) return false
    }
    if (tf) {
      const rtf = String(r.targetTimeframe || '').trim().toUpperCase()
      if (rtf !== tf) return false
    }
    if (trend) {
      const rt = String(r.targetTrend || '').trim().toLowerCase()
      if (rt !== trend) return false
    }
    if (type) {
      const ot = String(r.opportunityType || '').trim().toLowerCase()
      if (!ot.includes(type)) return false
    }
    return true
  })
}
